import React from "react";
import { Loader2 } from "lucide-react";

/**
 * ToolChip — Dreamina-style icon-forward tool chip.
 *
 * Two shapes:
 *  - "pill"   → primary actions (Generate, Restyle, Turntable). Full-width by default.
 *  - "square" → tool-grid chips (Split, Trim, Zoom, Sheet PNG, Rig Pack, etc.).
 *
 * Accents: gold (#F2C94C) · cyan (#00E5FF) · red (#EB5757) · muted (default).
 * Hover: subtle glow ring + soft translate + tint. Active: solid fill.
 *
 * Compose them in a <Toolbar> group; each chip is standalone-usable too.
 */

const ACCENT = {
  gold:  { fg: "#F2C94C", ring: "rgba(242,201,76,0.55)", glow: "rgba(242,201,76,0.28)", soft: "rgba(242,201,76,0.10)" },
  cyan:  { fg: "#00E5FF", ring: "rgba(0,229,255,0.55)",  glow: "rgba(0,229,255,0.28)",  soft: "rgba(0,229,255,0.10)"  },
  red:   { fg: "#EB5757", ring: "rgba(235,87,87,0.55)",  glow: "rgba(235,87,87,0.28)",  soft: "rgba(235,87,87,0.10)"  },
  muted: { fg: "#A09B8C", ring: "rgba(255,255,255,0.15)", glow: "rgba(255,255,255,0.08)", soft: "rgba(255,255,255,0.04)" },
};

export const ToolChip = React.forwardRef(function ToolChip(
  {
    icon: Icon,
    label,
    sublabel,
    onClick,
    disabled = false,
    loading = false,
    active = false,
    shape = "square",           // "pill" | "square"
    variant = "ghost",          // "ghost" | "solid" | "outline"
    accent = "muted",
    size = "md",                // "sm" | "md" | "lg"
    fullWidth = false,
    className = "",
    badge = null,
    testId,
    title,
    children,
    ...rest
  },
  ref
) {
  const a = ACCENT[accent] || ACCENT.muted;
  const isPill = shape === "pill";
  const sizeCls = {
    sm: isPill ? "px-4 py-2 text-[10px]"       : "px-3 py-2 min-w-[64px] text-[10px]",
    md: isPill ? "px-6 py-3 text-[11px]"       : "px-3 py-2.5 min-w-[76px] text-[10px]",
    lg: isPill ? "px-7 py-4 text-xs"           : "px-4 py-3 min-w-[92px] text-[11px]",
  }[size];

  const shapeCls = isPill ? "rounded-full" : "rounded-2xl";
  const gapCls   = isPill ? "gap-2 flex-row" : "gap-1.5 flex-col";

  // Base surface
  let bg = "bg-white/[0.03] hover:bg-white/[0.06]";
  let border = "border border-white/[0.08]";
  let text = "text-[#E8E4D8]";
  let iconColor = a.fg;

  if (variant === "solid" || active) {
    bg = "";  // filled via inline style
    border = "border border-transparent";
    text = "text-[#0A0A0A]";
    iconColor = "#0A0A0A";
  } else if (variant === "outline") {
    bg = "bg-transparent hover:bg-white/[0.04]";
    border = "";
  }

  const activeShadow = active || variant === "solid"
    ? `0 0 24px -4px ${a.glow}, inset 0 0 0 1px ${a.ring}`
    : `0 0 0 rgba(0,0,0,0)`;

  const style = {
    boxShadow: activeShadow,
    backgroundColor: (variant === "solid" || active) ? a.fg : undefined,
  };

  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      title={title || label}
      style={style}
      className={[
        "group relative inline-flex items-center justify-center overflow-hidden",
        gapCls, shapeCls, sizeCls, bg, border, text,
        "font-mono-hy tracking-[0.08em]",
        "transition-all duration-300 ease-out",
        "hover:-translate-y-0.5",
        // Hover glow ring for non-active states
        !active && variant !== "solid"
          ? `hover:shadow-[0_0_28px_-6px_${a.glow}] hover:border-[${a.ring}]`
          : "",
        disabled ? "opacity-50 cursor-not-allowed hover:translate-y-0 hover:shadow-none" : "cursor-pointer",
        fullWidth ? "w-full" : "",
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {/* Subtle hover gradient wash — pointer-events-none */}
      {!active && variant !== "solid" && !disabled && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(60% 80% at 50% 50%, ${a.soft} 0%, transparent 70%)` }}
        />
      )}

      {/* Icon */}
      {Icon && (
        <span className="relative flex items-center justify-center">
          {loading
            ? <Loader2 className={isPill ? "w-4 h-4 animate-spin" : "w-4 h-4 animate-spin"} style={{ color: iconColor }} />
            : <Icon className={isPill ? "w-4 h-4" : "w-[18px] h-[18px]"} style={{ color: iconColor }} />}
          {badge && (
            <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#F2C94C] text-[#0A0A0A] text-[8px] font-bold flex items-center justify-center">
              {badge}
            </span>
          )}
        </span>
      )}

      {/* Label + sublabel */}
      {(label || children || sublabel) && (
        <span className={`relative flex ${isPill ? "flex-row items-center gap-2" : "flex-col items-center leading-tight"}`}>
          <span className={isPill ? "" : "text-[10px]"}>{label || children}</span>
          {sublabel && !isPill && (
            <span className="text-[8px] opacity-60 mt-0.5">{sublabel}</span>
          )}
        </span>
      )}
    </button>
  );
});

export default ToolChip;
